import React, { useState, useEffect } from "react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
const MaterialCreationLoader = ({
  open,
  title = "Processing request",
  message = "Please wait while we prepare the material workflow.",
  variant = "processing",
}) => {
  const isDownload = variant === "download";
  const accent = isDownload
    ? {
        icon: <FileDownloadOutlinedIcon />,
        ring: "conic-gradient(from 90deg, #0f766e, #22c55e, #2563eb, #0f766e)",
        bar: "linear-gradient(90deg, #0f766e, #22c55e)",
        iconColor: "#0f766e",
        iconBg: "#ecfdf5",
        title: title === "Processing request" ? "Preparing download" : title,
        message:
          message === "Please wait while we prepare the material workflow."
            ? "Generating your Excel file and starting the download."
            : message,
      }
    : {
        icon: <Inventory2OutlinedIcon />,
        ring: "conic-gradient(from 90deg, #2563eb, #0f766e, #f59e0b, #2563eb)",
        bar: "linear-gradient(90deg, #2563eb, #0f766e)",
        iconColor: "#2563eb",
        iconBg: "#eff6ff",
        title,
        message,
      };

  useEffect(() => {
    const fontId = "lato-font-link";
    if (!document.getElementById(fontId)) {
      const link = document.createElement("link");
      link.id = fontId;
      link.href =
        "https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
  }, []);

  const outerTheme = useTheme();
  const localTheme = createTheme(outerTheme, {
    typography: {
      ...outerTheme.typography,
      fontFamily: "Lato, sans-serif",
    },
  });

  return (
    <ThemeProvider theme={localTheme}>
      <Backdrop
        open={open}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 9999,
          backgroundColor: "rgba(248, 250, 252, 0.74)",
          backdropFilter: "blur(6px)",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "calc(100% - 32px)",
          maxWidth: 380,
          p: 3,
          borderRadius: "8px",
          border: "1px solid #dbefe0",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.18)",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.96) 100%)",
        }}
      >
        <Stack spacing={2.25} alignItems="center" textAlign="center">
          <Box
            sx={{
              position: "relative",
              width: 84,
              height: 84,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                background: accent.ring,
                animation: "mctLoaderSpin 1.05s linear infinite",
                "@keyframes mctLoaderSpin": {
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 7,
                borderRadius: "50%",
                backgroundColor: "#ffffff",
                boxShadow: "inset 0 0 0 1px #e2e8f0",
              }}
            />
            <Box
              sx={{
                position: "relative",
                width: 46,
                height: 46,
                display: "grid",
                placeItems: "center",
                borderRadius: "50%",
                color: accent.iconColor,
                backgroundColor: accent.iconBg,
                animation: "mctLoaderPulse 1.4s ease-in-out infinite",
                "@keyframes mctLoaderPulse": {
                  "0%, 100%": { transform: "scale(1)", opacity: 1 },
                  "50%": { transform: "scale(0.92)", opacity: 0.78 },
                },
              }}
            >
              {accent.icon}
            </Box>
          </Box>

          <Box>
            <Typography
              sx={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}
            >
              {accent.title}
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
              {accent.message}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              height: 6,
              borderRadius: 999,
              overflow: "hidden",
              backgroundColor: "#e2e8f0",
            }}
          >
            <Box
              sx={{
                width: "44%",
                height: "100%",
                borderRadius: 999,
                background: accent.bar,
                animation: "mctLoaderBar 1.15s ease-in-out infinite",
                "@keyframes mctLoaderBar": {
                  "0%": { transform: "translateX(-110%)" },
                  "100%": { transform: "translateX(260%)" },
                },
              }}
            />
          </Box>
        </Stack>
      </Paper>
    </Backdrop>
    </ThemeProvider>
  );
};

export default MaterialCreationLoader;
